// [S-240 / S-242] Tenant preferences hooks — Look & feel + Notifications.
// Backed by the admin endpoints in apps/admin/src/routes/preferences.ts:
//   GET    /tenants/:id/preferences
//   PUT    /tenants/:id/preferences/branding
//   PUT    /tenants/:id/preferences/notifications
//
// Branding has a closed palette ("blue" | "violet" | ...) so the UI
// can map directly to design-system color tokens without exposing a
// hex picker (which would explode the WCAG-contrast surface).
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi, useApiBlob } from "./api";

export const BRANDING_COLORS = [
  "blue",
  "violet",
  "teal",
  "amber",
  "slate",
  "crimson",
] as const;
export type BrandingPrimaryColor = (typeof BRANDING_COLORS)[number];

export const NOTIFICATION_EVENTS = [
  "template_certified",
  "template_deprecated",
  "template_under_review",
  "ingestion_failed",
  "attestation_due",
] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export type Branding = {
  primaryColor: BrandingPrimaryColor;
  darkMode: boolean;
};

export type Notifications = {
  recipients: string[];
  webhookUrl: string | null;
  events: NotificationEvent[];
};

export type TenantPreferences = {
  tenantId: string;
  branding: Branding;
  notifications: Notifications;
  updatedAt: string; // ISO
  updatedBy: string;
};

export const PREFERENCES_KEY = ["tenant", "preferences"] as const;

export function useTenantPreferences(
  tenantId: string
): UseQueryResult<TenantPreferences, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...PREFERENCES_KEY, tenantId] as const,
    enabled: tenantId.length > 0,
    queryFn: () =>
      api<TenantPreferences>(
        `/tenants/${encodeURIComponent(tenantId)}/preferences`
      ),
  });
}

export function useUpdateBranding(): UseMutationResult<
  TenantPreferences,
  Error,
  { tenantId: string; branding: Branding }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, branding }) =>
      api<TenantPreferences>(
        `/tenants/${encodeURIComponent(tenantId)}/preferences/branding`,
        { method: "PUT", body: branding }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...PREFERENCES_KEY, tenantId] });
    },
  });
}

export function useUpdateNotifications(): UseMutationResult<
  TenantPreferences,
  Error,
  { tenantId: string; notifications: Notifications }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, notifications }) =>
      api<TenantPreferences>(
        `/tenants/${encodeURIComponent(tenantId)}/preferences/notifications`,
        { method: "PUT", body: notifications }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...PREFERENCES_KEY, tenantId] });
    },
  });
}

// [S-243] Audit log CSV export. Calls the admin endpoint which streams
// a CSV with the caller's tenant rows in [from, to]. Uses useApiBlob
// (S-213 added it for DOCX export) so the dev IAP shim + auth headers
// are applied consistently.
export function useExportAuditLog(): UseMutationResult<
  { filename: string },
  Error,
  { fromIso?: string; toIso?: string }
> {
  const blobApi = useApiBlob("admin");
  return useMutation({
    mutationFn: async ({ fromIso, toIso }) => {
      const params = new URLSearchParams();
      if (fromIso !== undefined) params.set("from", fromIso);
      if (toIso !== undefined) params.set("to", toIso);
      const qs = params.toString();
      const path = `/tenants/me/audit/export${qs.length > 0 ? `?${qs}` : ""}`;
      const { blob, filename } = await blobApi(path);
      const finalName = filename ?? "csai-audit.csv";
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      return { filename: finalName };
    },
  });
}
